import { useEffect, useState } from "react";
import { api, ApiError, type Portfolio } from "../lib/api";
import { InquirySlideOver } from "../components/InquirySlideOver";
import { OnboardingWizard } from "../components/dashboard/OnboardingWizard";
import { GettingStartedGuide } from "../components/dashboard/GettingStartedGuide";
import { ActiveDashboard } from "../components/dashboard/ActiveDashboard";

export function DashboardPage() {
  const [portfolio, setPortfolio] = useState<Portfolio | null | "loading">("loading");
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [stats, setStats] = useState<{
    bookings_total: number;
    bookings_upcoming: number;
    bookings_inquiry: number;
    galleries_total: number;
    galleries_public: number;
    clients_total: number;
    leads_total: number;
    packages_public: number;
  } | null>(null);

  useEffect(() => {
    // Load portfolio state first — determines which UI to show.
    api
      .getMyPortfolio()
      .then((p) => setPortfolio(p))
      .catch((err) => {
        if (err instanceof ApiError && err.status === 404) setPortfolio(null);
        else setPortfolio(null);
      });

    // Load stats in parallel — used by all states except wizard.
    Promise.all([
      api.listBookings(),
      api.listGalleries(),
      api.listClients(),
      api.listLeads().catch(() => [] as Awaited<ReturnType<typeof api.listLeads>>),
      api.listPackages().catch(() => [] as Awaited<ReturnType<typeof api.listPackages>>),
    ])
      .then(([bookings, galleries, clients, leads, pkgs]) => {
        const now = new Date();
        setStats({
          bookings_total: bookings.length,
          bookings_upcoming: bookings.filter(
            (b) =>
              (b.Status === "booked" || b.Status === "inquiry") &&
              b.ScheduledAt &&
              new Date(b.ScheduledAt) > now
          ).length,
          bookings_inquiry: bookings.filter((b) => b.Status === "inquiry").length,
          galleries_total: galleries.length,
          galleries_public: galleries.filter((g) => g.IsPublic).length,
          clients_total: clients.length,
          leads_total: leads.filter(l => !l.HandledAt).length,
          packages_public: pkgs.filter((p) => p.IsPublic).length,
        });
      })
      .catch(() => {/* non-fatal */});
  }, []);

  // Still fetching portfolio.
  if (portfolio === "loading") {
    return (
      <div style={{ padding: "3rem 0", textAlign: "center" }}>
        <p style={{ color: "var(--muted)" }}>Loading…</p>
      </div>
    );
  }

  // No portfolio yet — show the onboarding wizard.
  if (portfolio === null) {
    return <OnboardingWizard onCreated={(p) => setPortfolio(p)} />;
  }

  // Portfolio exists — decide between "getting started" and "active" dashboards.
  const hasActivity =
    (stats?.galleries_total ?? 0) > 0 ||
    (stats?.clients_total ?? 0) > 0 ||
    (stats?.bookings_total ?? 0) > 0 ||
    (stats?.leads_total ?? 0) > 0;

  if (!hasActivity || !portfolio.IsPublished) {
    return (
      <>
        <InquirySlideOver
          open={inquiryOpen}
          onClose={() => setInquiryOpen(false)}
          onCreated={() => setInquiryOpen(false)}
        />
        <GettingStartedGuide
          portfolio={portfolio}
          onNewInquiry={() => setInquiryOpen(true)}
          stats={{
            galleries: stats?.galleries_total ?? 0,
            clients: stats?.clients_total ?? 0,
            leads: stats?.leads_total ?? 0,
            bookings: stats?.bookings_upcoming ?? 0,
            isPublished: portfolio.IsPublished,
          }}
        />
      </>
    );
  }

  return (
    <>
      <InquirySlideOver
        open={inquiryOpen}
        onClose={() => setInquiryOpen(false)}
        onCreated={() => setInquiryOpen(false)}
      />
      <ActiveDashboard
        portfolio={portfolio}
        onNewInquiry={() => setInquiryOpen(true)}
        stats={stats ?? {
          bookings_total: 0,
          bookings_upcoming: 0,
          bookings_inquiry: 0,
          galleries_total: 0,
          galleries_public: 0,
          clients_total: 0,
          leads_total: 0,
          packages_public: 0,
        }}
      />
    </>
  );
}
